/**
 * useStageLayout — unified seat-positioning hook for the council stage.
 *
 * Two shapes:
 *
 *   1. "circle"    — classic round-table; works for ≤ ~8 seats. Seats
 *                    are evenly distributed clockwise starting at 12
 *                    o'clock. Identical to the legacy
 *                    `useRoundtableLayout` output, kept here so the
 *                    edge renderer can consume one source of truth.
 *
 *   2. "boardroom" — 16:9 rectangular boardroom. Seat 0 (the
 *                    moderator) sits at the LEFT short edge head;
 *                    every other persona is split between the top
 *                    and bottom long edges, alternating to keep the
 *                    visual balance. This is what we need above
 *                    ~9 seats — the circle stops being readable.
 *
 * Coordinates are returned relative to the CONTAINER CENTER (the same
 * convention the existing `RoundtableStage` already uses with
 * `calc(50% + ${pos.x}px)` / `calc(50% + ${pos.y}px)`), so dropping
 * this hook in is a pure swap of the position computation.
 *
 * Why we keep `angle` even in boardroom mode
 * ------------------------------------------
 * The `TopologyEdgeRenderer` currently uses the polar angle to draw
 * curved edges. For boardroom mode we still expose an `angle` per
 * seat so the edge renderer falls back to STRAIGHT lines that look
 * sane in a rectangular layout. We compute `angle` as the polar
 * direction from the center to the seat — a useful side-effect is
 * that "moderator → person" arrows still point away from the head.
 */
import { useMemo } from "react";

export interface StageSeatPosition {
  x: number;
  y: number;
  angle: number; // radians, 0 = right, -π/2 = top
}

export type StageShape = "circle" | "boardroom";

export interface UseStageLayoutOptions {
  seatCount: number;
  /** Container width in px. Required for both shapes. */
  width: number;
  /** Container height in px. For circle shapes pass the same value as width. */
  height: number;
  shape: StageShape;
  /** Polar start angle for circle mode. Defaults to top (-π/2). */
  startAngle?: number;
  /**
   * Distance (px) reserved at the rim for the avatar disc + speaking
   * halo + label stack. The radius is then `min(w,h)/2 - seatPadding`,
   * so larger avatars (e.g. 130 px in present mode) automatically push
   * the seats inwards instead of overflowing the stage and being
   * clipped by the parent `overflow-hidden` chain.
   *
   * Caller should compute as: `avatarSize / 2 + 30` (30 = halo + label
   * vertical bleed). Defaults to 60 to preserve legacy behaviour for
   * call sites that don't know the avatar size yet.
   */
  seatPadding?: number;
}

const _BOARDROOM_PADDING_X_RATIO = 0.06; // 6% horizontal inset
const _BOARDROOM_PADDING_Y_RATIO = 0.10; // 10% vertical inset
// Distance from the long edge to the seat avatar centre, as a
// fraction of container HEIGHT. Empirically tuned so a 64-px avatar
// straddles the table edge nicely without clipping into the table
// surface gradient.
const _BOARDROOM_SEAT_OFFSET_RATIO = 0.05;

/**
 * Build positions for a circular seating arrangement (legacy mode).
 */
function circlePositions(
  seatCount: number,
  radius: number,
  startAngle: number,
): StageSeatPosition[] {
  if (seatCount <= 0) return [];
  const out: StageSeatPosition[] = [];
  const step = (2 * Math.PI) / seatCount;
  for (let i = 0; i < seatCount; i++) {
    const angle = startAngle + i * step;
    out.push({
      x: radius * Math.cos(angle),
      y: radius * Math.sin(angle),
      angle,
    });
  }
  return out;
}

/**
 * Build positions for a 16:9 boardroom: moderator at the left head,
 * remaining personas alternating between top and bottom long edges.
 *
 * Member distribution (n = seatCount):
 *   - seat[0]            → left-head (moderator)
 *   - seat[1, 3, 5, ...] → top edge, left → right
 *   - seat[2, 4, 6, ...] → bottom edge, left → right
 * Alternating instead of "first half on top / second half on bottom"
 * keeps the seat-order visually contiguous when traffic walks the
 * table (turn N+1 sits directly across from turn N most of the time).
 */
function boardroomPositions(
  seatCount: number,
  width: number,
  height: number,
): StageSeatPosition[] {
  if (seatCount <= 0) return [];
  const out: StageSeatPosition[] = [];

  const padX = width * _BOARDROOM_PADDING_X_RATIO;
  const padY = height * _BOARDROOM_PADDING_Y_RATIO;
  // Container half-extents — coordinates are CENTER-relative.
  const halfW = width / 2;
  const halfH = height / 2;

  // Moderator sits at the left short edge, just outside the table
  // rim. We push them slightly inboard from the absolute container
  // edge so the avatar + label don't clip into the round-counter
  // pill at the top-right.
  const moderatorX = -halfW + padX * 0.45;
  const moderatorY = 0;
  out.push({ x: moderatorX, y: moderatorY, angle: Math.PI }); // facing right

  const memberCount = seatCount - 1;
  if (memberCount <= 0) return out;

  // Split members between top and bottom edges (alternating).
  const topIdxs: number[] = [];
  const bottomIdxs: number[] = [];
  for (let i = 0; i < memberCount; i++) {
    (i % 2 === 0 ? topIdxs : bottomIdxs).push(i);
  }

  const seatOffset = height * _BOARDROOM_SEAT_OFFSET_RATIO;
  const yTop = -halfH + padY - seatOffset;
  const yBottom = +halfH - padY + seatOffset;

  // Member x range: from just right of the moderator's column up
  // to just inside the right rim. We keep at least `padX` clearance
  // on each side so labels don't clip the container.
  const xLeft = moderatorX + padX * 1.6;
  const xRight = +halfW - padX * 0.7;

  // Helper: distribute n positions evenly between two x coordinates,
  // CENTERED on the available span (no seats at the exact edges).
  const distribute = (count: number): number[] => {
    if (count === 0) return [];
    if (count === 1) return [(xLeft + xRight) / 2];
    const span = xRight - xLeft;
    const step = span / count; // gap between seat slots
    return Array.from({ length: count }, (_, i) => xLeft + step * (i + 0.5));
  };

  const topX = distribute(topIdxs.length);
  const bottomX = distribute(bottomIdxs.length);

  // Assemble the output array IN seat-order so consumers can use
  // index lookups directly (seat 0 = out[0], seat 5 = out[5]).
  const slot = new Array<StageSeatPosition | null>(seatCount).fill(null);
  slot[0] = { x: moderatorX, y: moderatorY, angle: Math.PI };
  topIdxs.forEach((memberIdx, i) => {
    const seatIdx = memberIdx + 1;
    slot[seatIdx] = {
      x: topX[i] ?? 0,
      y: yTop,
      angle: -Math.PI / 2,
    };
  });
  bottomIdxs.forEach((memberIdx, i) => {
    const seatIdx = memberIdx + 1;
    slot[seatIdx] = {
      x: bottomX[i] ?? 0,
      y: yBottom,
      angle: Math.PI / 2,
    };
  });

  // Replace `out` with the slot array; everything is now ordered.
  out.length = 0;
  for (const s of slot) {
    out.push(s ?? { x: 0, y: 0, angle: 0 });
  }
  return out;
}

/**
 * Pick the best stage shape for `seatCount`.
 *
 * Threshold = 9. Eight personas still fit a round table cleanly
 * (45° per seat, plenty of room for a 64-px avatar + label). At 9
 * we hit ~40° per seat and labels start overlapping at typical
 * container widths (340-560 px), so we flip to the boardroom.
 */
export function pickStageShape(seatCount: number): StageShape {
  return seatCount >= 9 ? "boardroom" : "circle";
}

/**
 * Compute the per-seat label budget (max horizontal width in px) given
 * the chosen stage shape and seat count. Used by `RoundtableStage` to
 * clamp the displayName / expertise label width so adjacent personas
 * don't overlap when the boardroom packs 5+ seats per long edge.
 *
 * Returns `undefined` for circle mode (callers fall back to the
 * existing Tailwind `max-w-*` defaults), and per-seat numeric budgets
 * for boardroom mode:
 *   • seat 0 (moderator at the left head): generous, ~160 px
 *   • seat 1..N (top/bottom edge members): `step − 8 px` clearance,
 *     never below `72 px` (so a one-line ellipsised expertise tag
 *     remains legible).
 *
 * The math here MUST mirror `boardroomPositions` exactly — any drift
 * means the label budget no longer matches the slot the seat sits in.
 */
export function computeLabelBudgets(
  shape: StageShape,
  seatCount: number,
  width: number,
): number[] | undefined {
  if (shape !== "boardroom" || seatCount <= 0) return undefined;
  const padX = width * _BOARDROOM_PADDING_X_RATIO;
  const halfW = width / 2;
  const moderatorX = -halfW + padX * 0.45;
  const xLeft = moderatorX + padX * 1.6;
  const xRight = +halfW - padX * 0.7;
  const span = Math.max(0, xRight - xLeft);

  const memberCount = seatCount - 1;
  const topCount = Math.ceil(memberCount / 2);
  const bottomCount = Math.floor(memberCount / 2);
  // Step per row = span / row-count. The denser row dictates the
  // tighter clamp, so we use max(top, bottom) as the divisor for
  // both rows. Same width on every member keeps the optical rhythm
  // consistent (no jarring "this label is bigger than that one").
  const denserRow = Math.max(topCount, bottomCount);
  const step = denserRow > 0 ? span / denserRow : span;
  const memberBudget = Math.max(72, Math.floor(step - 8));

  const budgets = new Array<number>(seatCount).fill(memberBudget);
  // Moderator sits in the (mostly-empty) left short edge — we have
  // padX × ~1.6 of horizontal real estate to play with, so give the
  // longest expertise tag a chance to read on one line.
  //
  // SAFETY CLAMP (2026-07-22 — "Prof. Dr. ..." clipping bug):
  // the label is rendered CENTERED on `moderatorX` (see
  // `boardroomPositions`, seat 0 = `-halfW + padX * 0.45`), so it bleeds
  // `budgets[0] / 2` px to the LEFT of that anchor. The stage container
  // itself renders with `overflow-visible`, but its ancestor
  // (`RoundtableOrchestrator`'s "Inner container") clips with
  // `overflow-hidden` and only reserves ~16-24px of outer padding
  // (`stagePadding`). An unclamped `padX * 3.2` (e.g. 120-190px at
  // typical stage widths) bleeds the label 40-70px past the stage's own
  // left edge — well past that padding — so long titles ("Prof. Dr.
  // Berna Kutlu") got silently clipped by the ancestor instead of
  // ellipsising. Clamp the budget so the label's left edge never
  // crosses more than `SAFE_BLEED_PX` past the stage boundary; the
  // `truncate` class on the label (`AgentAvatar.tsx`) then degrades
  // gracefully with an ellipsis instead of a hard visual cutoff.
  const SAFE_BLEED_PX = 20;
  const moderatorAnchorOffsetFromEdge = padX * 0.45;
  const moderatorMaxSafeBudget = Math.floor(2 * (moderatorAnchorOffsetFromEdge + SAFE_BLEED_PX));
  budgets[0] = Math.max(72, Math.min(Math.floor(padX * 3.2), moderatorMaxSafeBudget));
  return budgets;
}

export function useStageLayout({
  seatCount,
  width,
  height,
  shape,
  startAngle = -Math.PI / 2,
  seatPadding = 60,
}: UseStageLayoutOptions): StageSeatPosition[] {
  return useMemo<StageSeatPosition[]>(() => {
    if (shape === "boardroom") {
      return boardroomPositions(seatCount, width, height);
    }
    // Circle mode — radius is derived from the smaller container
    // dimension so we never overflow when the parent isn't square.
    // `seatPadding` is the avatar-aware rim reserve (avatarRadius +
    // halo bleed). Without this, scaling avatars in present mode
    // (~130 px) clips against the stage box because the legacy
    // sentinel pad of 60 px assumes ≤72-px discs.
    const radius = Math.min(width, height) / 2 - seatPadding;
    return circlePositions(seatCount, radius, startAngle);
  }, [seatCount, width, height, shape, startAngle, seatPadding]);
}
